// ─── Angular Core ────────────────────────────────
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpHeaders,
    HttpErrorResponse,
    HttpContextToken,
} from '@angular/common/http'
import { DOCUMENT } from '@angular/common'
import { Injectable, Injector } from '@angular/core'

// ─── Ionic & Capacitor ───────────────────────────
import { Storage } from '@ionic/storage-angular'

// ─── RxJS ────────────────────────────────────────
import { Observable, from, throwError } from 'rxjs'
import { switchMap, catchError } from 'rxjs/operators'

// ─── Project (Alias) ─────────────────────────────
import { EmployeeService } from '@/app/domains/auth/employee/data-access/employee.service'
import { ICompany, IBranch, IEmployee } from '@/app/interfaces/interfaces'
import { environment } from '@/environments/environment'
import { apiBaseUrl } from '@/app/core/config/api-endpoints'
import { SKIP_AUTH_RENEWAL } from '@/app/core/http/http-context.tokens'

// src/app/interceptors/interceptor.service.ts

// ✅ Fuente de verdad: microservicio AUTH, vía el mismo gateway que el resto
const AUTH_URL = apiBaseUrl('auth')

// Header anti-loop para reintentos
const AUTH_RETRY_HEADER = 'x-auth-retry'

// Anti-loop para reintentos contra el gateway. Va en HttpContext y no como header:
// un header custom rompería el preflight CORS del gateway (Access-Control-Allow-Headers).
const GATEWAY_AUTH_RETRIED = new HttpContextToken<boolean>(() => false)

// Margen para desfase de reloj cliente/servidor al decidir si el JWT ya expiró.
const TOKEN_EXPIRY_SKEW_MS = 30_000

/**
 * Interceptor (AUTH-first, sin legacy):
 * - Adjunta Authorization: Bearer <backend_token> a todas las requests (incluido ERP) excepto endpoints internos de AUTH.
 * - Adjunta x-company / x-branch usando UID (string) para dar contexto multi-tenant.
 * - Si AUTH responde 401/403/AUTH_INVALID_TOKEN/TOKEN_EXPIRED y la request era a AUTH,
 *   intenta renovar (firebase) y reintenta 1 vez.
 * - Si un microservicio del gateway responde 401 y el backend_token ya expiró
 *   (nginx auth_request lo rechaza), renueva y reintenta 1 vez.
 *
 * Nota:
 * - Eliminamos totalmente x-token (legacy ERP) y lectura de 'token'.
 */
@Injectable({ providedIn: 'root' })
export class InterceptorService implements HttpInterceptor {
    // ✅ Keys “canon”
    private readonly KEY_BACKEND_TOKEN = 'backend_token'
    private readonly KEY_COMPANY = 'company'
    private readonly KEY_BRANCH = 'branch'

    constructor(
        private storage: Storage,
        private injector: Injector
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // GCS signed URLs must not receive extra headers — pass through untouched
        if (this.isGcsRequest(req.url)) {
            return next.handle(req)
        }

        return from(this.getData()).pipe(
            switchMap(({ backendToken, branch, company, employee }) => {
                let headers = req.headers || new HttpHeaders()

                const isAuth = this.isMicroAuthRequest(req.url)
                const isErp = this.isErpRequest(req.url)
                const isFiscal = this.isFiscalRequest(req.url)
                const isPayments = this.isPaymentsRequest(req.url)
                const isPlatform = this.isPlatformRequest(req.url)
                const isResource = this.isResourceRequest(req.url)
                const isMasterData = this.isMasterDataRequest(req.url)
                const isMedia = this.isMediaRequest(req.url)

                const isGatewayMicroservice = this.isGatewayMicroserviceRequest(req.url)

                const isInternalAuthEndpoint = this.isInternalAuthEndpoint(req.url)
                const isRefreshEndpoint = this.isAuthRefreshEndpoint(req.url)

                // Common context variables
                const companyUid =
                    (company as any)?.uid ??
                    (company as any)?.company_uid ??
                    (company as any)?.uuid ??
                    null
                const branchUid =
                    (branch as any)?.uid ??
                    (branch as any)?.branch_uid ??
                    (branch as any)?.uuid ??
                    null
                const employeeUid =
                    (employee as any)?.uid ??
                    (employee as any)?.employee_uid ??
                    (employee as any)?.uuid ??
                    null

                if (isAuth) {
                    if (backendToken && (!isInternalAuthEndpoint || isRefreshEndpoint)) {
                        headers = headers.set('Authorization', `Bearer ${backendToken}`)
                    }
                    if (branchUid) {
                                headers = headers.set('x-branch', String(branchUid))
                                headers = headers.set('x-branch-uid', String(branchUid))
                            }
                    if (companyUid) {
                        headers = headers.set('x-company', String(companyUid))
                        headers = headers.set('x-company-uid', String(companyUid))
                    }
                    headers = headers.delete('x-employee-uid')
                } else if (isGatewayMicroservice) {
                    if (backendToken) {
                        headers = headers.set('Authorization', `Bearer ${backendToken}`)
                    } else {
                        headers = headers.delete('Authorization')
                        if (!environment.production) {
                            const which = isErp
                                ? 'ERP'
                                : isPayments
                                  ? 'Payments'
                                  : isPlatform
                                    ? 'Platform'
                                    : isResource
                                      ? 'Resource'
                                      : isMasterData
                                        ? 'MasterData'
                                        : isFiscal
                                          ? 'Fiscal'
                                          : isMedia
                                            ? 'Media'
                                            : 'Marketplace'
                            console.warn(`[${which} Request] backend_token missing`)
                        }
                    }

                    // Panel de plataforma (/fiscal/admin): la empresa es la que el administrador ELIGE en la propia llamada
                    // (?company_uid=). Mandar además la empresa activa de la sesión haría que el gateway valide la
                    // pertenencia a una empresa que no es el objetivo (y responda 403 si quedó una guardada de antes).
                    // Catálogos GLOBALES (categorías/atributos de resource, medidas de master-data) vistos desde el panel de plataforma
                    // (/admin/*): no dependen de ninguna empresa y los servicios ya aceptan una sesión sin empresa. Mandar la empresa
                    // activa de la sesión haría que el gateway valide la pertenencia (403 si quedó una guardada que no es del usuario).
                    const isPlatformAdminCall =
                        this.isFiscalPlatformAdminRequest(req.url) ||
                        ((isResource || isMasterData) && this.isInPlatformAdminArea())
                    if (branchUid && !isPlatformAdminCall) {
                                headers = headers.set('x-branch', String(branchUid))
                                headers = headers.set('x-branch-uid', String(branchUid))
                            }
                    if (companyUid && !isPlatformAdminCall) {
                        headers = headers.set('x-company', String(companyUid))
                        headers = headers.set('x-company-uid', String(companyUid))
                    }
                    if (employeeUid && !isPlatformAdminCall) headers = headers.set('x-employee-uid', String(employeeUid))
                } else {
                    // Fallback for any other admin or legacy calls
                    const isAdminApi = req.url.includes('/admin/')
                    if (!isAdminApi) {
                        if (backendToken)
                            headers = headers.set('Authorization', `Bearer ${backendToken}`)
                        if (branchUid) {
                                headers = headers.set('x-branch', String(branchUid))
                                headers = headers.set('x-branch-uid', String(branchUid))
                            }
                        if (companyUid) {
                            headers = headers.set('x-company', String(companyUid))
                            headers = headers.set('x-company-uid', String(companyUid))
                        }
                        if (employeeUid) headers = headers.set('x-employee-uid', String(employeeUid))
                    }
                }

                const requestWithHeaders = req.clone({ headers })

                // Debug logging for Media requests
                if (!environment.production && isMedia) {
                    console.log(`[Media Request] ${req.url}`)
                    console.log(`[Media Request] companyUid resolved: ${companyUid}`)
                    console.log(
                        `[Media Request] x-company on cloned req: ${requestWithHeaders.headers.get('x-company')}`
                    )
                    console.log(
                        `[Media Request] Authorization on cloned req: ${requestWithHeaders.headers.has('Authorization') ? 'present' : 'MISSING'}`
                    )
                }

                // Logging for Fiscal requests
                if (!environment.production && isFiscal) {
                    console.log(`[Fiscal Request] ${req.url}`)
                    console.log(`[Fiscal Request] hasBackendToken: ${!!backendToken}`)
                    console.log(`[Fiscal Request] hasCompany: ${!!companyUid} (${companyUid})`)
                    console.log(`[Fiscal Request] hasBranch: ${!!branchUid} (${branchUid})`)
                }

                // Secure local logging for ERP requests
                if (!environment.production && isErp) {
                    console.log(`[ERP Request] ${req.url}`)
                    console.log(`[ERP Request] hasBackendToken: ${!!backendToken}`)
                    if (backendToken) {
                        console.log(`[ERP Request] tokenLength: ${backendToken.length}`)
                        console.log(
                            `[ERP Request] tokenPrefix: ${backendToken.substring(0, 12)}...`
                        )
                        try {
                            const payload = JSON.parse(atob(backendToken.split('.')[1]))
                            console.log(`[ERP Request] decodedPayload.iss: ${payload.iss}`)
                            console.log(`[ERP Request] decodedPayload.aud: ${payload.aud}`)
                            console.log(
                                `[ERP Request] decodedPayload.employee_uid: ${payload.employee_uid || payload.sub}`
                            )
                            console.log(`[ERP Request] decodedPayload.exp: ${payload.exp}`)
                        } catch (e) {
                            console.log(`[ERP Request] could not decode payload: ${e}`)
                        }
                    }
                    console.log(`[ERP Request] hasCompany: ${!!companyUid}`)
                    console.log(`[ERP Request] hasBranch: ${!!branchUid}`)
                    console.log(`[ERP Request] hasEmployeeUid: ${!!employeeUid}`)
                }

                return next
                    .handle(requestWithHeaders)
                    .pipe(
                        catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
                    )
            })
        )
    }

    private handleError(
        error: any,
        originalReq: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const status = error?.status
        const code = error?.error?.code || error?.code

        const isAuthError =
            status === 401 ||
            status === 403 ||
            code === 'AUTH_INVALID_TOKEN' ||
            code === 'TOKEN_EXPIRED'

        const isAuthRequest = this.isMicroAuthRequest(originalReq.url)
        const isInternalAuthEndpoint = this.isInternalAuthEndpoint(originalReq.url)

        // anti-loop
        const alreadyRetried = originalReq.headers?.get(AUTH_RETRY_HEADER) === '1'

        // Request hecha desde dentro de la renovación: renovar de nuevo sería esperarse a sí misma
        if (originalReq.context.get(SKIP_AUTH_RENEWAL)) return throwError(() => error)

        // Token vigente pero sin scope (login-oauth con 2+ sucursales lo emite sin company_uid):
        // los micros no pueden resolver el tenant. Se re-scopea a la sucursal activa y se reintenta 1 vez.
        if (
            status === 400 &&
            error?.error?.error?.code === 'TENANT_CONTEXT_MISSING' &&
            this.isGatewayMicroserviceRequest(originalReq.url) &&
            !originalReq.context.get(GATEWAY_AUTH_RETRIED)
        ) {
            const employeeService = this.injector.get(EmployeeService)
            return from(employeeService.rescopeBackendToken()).pipe(
                switchMap((scoped) => {
                    if (!scoped) return throwError(() => error)

                    originalReq.context.set(GATEWAY_AUTH_RETRIED, true)
                    return this.intercept(originalReq, next)
                })
            )
        }

        // Gateway (ERP, master-data, …): nginx auth_request responde 401 con el backend_token
        // vencido y ningún microservicio lo renueva. Solo se renueva si el token realmente
        // expiró — un 401 con token vigente (p. ej. MISSING_TENANT) no se arregla re-logueando.
        if (
            status === 401 &&
            this.isGatewayMicroserviceRequest(originalReq.url) &&
            !originalReq.context.get(GATEWAY_AUTH_RETRIED)
        ) {
            return from(this.isBackendTokenExpired()).pipe(
                switchMap((expired) => {
                    if (!expired) return throwError(() => error)

                    const employeeService = this.injector.get(EmployeeService)
                    return from(employeeService.renewBackendTokenByFirebase()).pipe(
                        switchMap((valid) => {
                            if (!valid) return throwError(() => error)

                            // intercept() vuelve a leer el token renovado y rearma los headers
                            originalReq.context.set(GATEWAY_AUTH_RETRIED, true)
                            return this.intercept(originalReq, next)
                        })
                    )
                })
            )
        }

        if (isAuthError && isAuthRequest && !isInternalAuthEndpoint && !alreadyRetried) {
            // ✅ Lazy inject para evitar ciclos DI
            const employeeService = this.injector.get(EmployeeService)

            return from(employeeService.renewBackendTokenByFirebase()).pipe(
                switchMap((valid) => {
                    if (!valid) return throwError(() => error)

                    // reintento único con token actualizado
                    return from(this.getData()).pipe(
                        switchMap(({ backendToken, branch, company, employee }) => {
                            let headers = originalReq.headers || new HttpHeaders()

                            headers = headers.set(AUTH_RETRY_HEADER, '1')

                            const isInternal = this.isInternalAuthEndpoint(originalReq.url)
                            const isRefresh = this.isAuthRefreshEndpoint(originalReq.url)

                            if (backendToken && (!isInternal || isRefresh)) {
                                headers = headers.set('Authorization', `Bearer ${backendToken}`)
                            }

                            const companyUid =
                                (company as any)?.uid ??
                                (company as any)?.company_uid ??
                                (company as any)?.uuid ??
                                null
                            const branchUid =
                                (branch as any)?.uid ??
                                (branch as any)?.branch_uid ??
                                (branch as any)?.uuid ??
                                null
                            const employeeUid =
                                (employee as any)?.uid ??
                                (employee as any)?.employee_uid ??
                                (employee as any)?.uuid ??
                                null

                            if (branchUid) {
                                headers = headers.set('x-branch', String(branchUid))
                                headers = headers.set('x-branch-uid', String(branchUid))
                            }
                            if (companyUid) {
                                headers = headers.set('x-company', String(companyUid))
                                headers = headers.set('x-company-uid', String(companyUid))
                            }
                            headers = headers.delete('x-employee-uid')

                            const retriedReq = originalReq.clone({ headers })
                            return next.handle(retriedReq)
                        })
                    )
                })
            )
        }

        return throwError(() => error)
    }

    private isGatewayMicroserviceRequest(url: string): boolean {
        return (
            this.isErpRequest(url) ||
            this.isPaymentsRequest(url) ||
            this.isPlatformRequest(url) ||
            this.isResourceRequest(url) ||
            this.isMasterDataRequest(url) ||
            this.isFiscalRequest(url) ||
            this.isMediaRequest(url) ||
            this.isMarketplaceRequest(url) ||
            this.isLocationRequest(url)
        )
    }

    /** true si el backend_token guardado ya venció (o no se puede leer su `exp`). */
    private async isBackendTokenExpired(): Promise<boolean> {
        const { backendToken } = await this.getData()
        if (!backendToken) return true
        try {
            const base64 = backendToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            const payload = JSON.parse(atob(base64))
            if (typeof payload?.exp !== 'number') return true
            return payload.exp * 1000 - TOKEN_EXPIRY_SKEW_MS <= Date.now()
        } catch (_) {
            return true
        }
    }

    private isMarketplaceRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/marketplace')
    }

    private isMediaRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/media')
    }

    /** Rutas de plataforma del servicio fiscal: /v1/fiscal/admin/... (la empresa va en ?company_uid=, no en la sesión). */
    /** ¿El usuario está en el panel de plataforma (/admin/...)? Rutas con prefijo de ruta (no hash). */
    private isInPlatformAdminArea(): boolean {
        try {
            const path = this.injector.get(DOCUMENT).location?.pathname ?? ''
            return /^\/admin(\/|$)/.test(path)
        } catch {
            return false
        }
    }

    private isFiscalPlatformAdminRequest(url: string): boolean {
        return /\/v1\/fiscal\/admin(\/|\?|$)/.test(url)
    }

    private isFiscalRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/fiscal')
    }

    private isMasterDataRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/master-data')
    }

    private isResourceRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/resource')
    }

    private isMicroAuthRequest(url: string): boolean {
        if (!url) return false
        if (AUTH_URL && url.startsWith(AUTH_URL)) return true
        if (url.includes('/v1/auth/')) return true
        return false
    }

    private isErpRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/erp')
    }

    private isPaymentsRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/payments')
    }

    private isPlatformRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/platform')
    }

    private isLocationRequest(url: string): boolean {
        if (!url) return false
        return url.includes('/v1/location')
    }

    private isAuthRefreshEndpoint(url: string): boolean {
        if (!url) return false
        return url.includes('/refresh-token') || url.includes('/v1/auth/refresh-token')
    }

    private isGcsRequest(url: string): boolean {
        if (!url) return false
        return url.includes('storage.googleapis.com')
    }

    private isInternalAuthEndpoint(url: string): boolean {
        if (!url) return false
        const isMe = url.includes('/v1/auth/me') || url.includes('/auth/me') || url.endsWith('/me')
        const isLoginOrRefresh =
            url.includes('/v1/auth/login') ||
            url.includes('/v1/auth/login-oauth') ||
            url.includes('/login-oauth') ||
            url.includes('/refresh-token') ||
            url.includes('/v1/auth/refresh-token')

        return isMe || isLoginOrRefresh
    }

    /**
     * Lee todo desde storage.
     * - backend_token: JWT emitido por AUTH (fuente de verdad)
     * - branch/company: objetos con uid. Si el storage no tiene uid (sesión pre-migración),
     *   se extrae del JWT que siempre incluye company.uid y branch.uid en su payload.
     */
    private async getData(): Promise<{
        backendToken: string | null
        branch: IBranch | null
        company: ICompany | null
        employee: IEmployee | null
    }> {
        const backendToken =
            (await this.storage.get(this.KEY_BACKEND_TOKEN)) ??
            (await this.storage.get('gm.backend.token')) ??
            null

        let branch: IBranch | null = (await this.storage.get(this.KEY_BRANCH)) ?? null
        let company: ICompany | null = (await this.storage.get(this.KEY_COMPANY)) ?? null
        const employee: IEmployee | null = (await this.storage.get('employee')) ?? null

        // El JWT del auth service incluye companies: [{ uid, branches: [{ uid }] }].
        // Si el objeto en storage no tiene uid (sesión del ERP antiguo sin migración UID),
        // extraemos el uid de la compañía/sucursal activa del JWT.
        if (backendToken && (!(company as any)?.uid || !(branch as any)?.uid)) {
            try {
                const jwtPayload = JSON.parse(atob(backendToken.split('.')[1]))
                const jwtCompanies: any[] = jwtPayload?.companies ?? []

                if (jwtCompanies.length > 0) {
                    // Intentar casar por nombre con la compañía en storage; si no, usar la primera
                    const storedName = (company as any)?.name
                    const jwtActiveCompany =
                        (storedName
                            ? jwtCompanies.find((c: any) => c.name === storedName)
                            : null) ?? jwtCompanies[0]

                    if (jwtActiveCompany?.uid && !(company as any)?.uid) {
                        company = { ...(company ?? {}), uid: jwtActiveCompany.uid } as ICompany
                    }

                    // Branch: misma lógica dentro de la compañía activa
                    const jwtBranches: any[] = jwtActiveCompany?.branches ?? []
                    if (jwtBranches.length > 0 && !(branch as any)?.uid) {
                        const storedBranchName = (branch as any)?.name
                        const jwtActiveBranch =
                            (storedBranchName
                                ? jwtBranches.find((b: any) => b.name === storedBranchName)
                                : null) ?? jwtBranches[0]

                        if (jwtActiveBranch?.uid) {
                            branch = { ...(branch ?? {}), uid: jwtActiveBranch.uid } as IBranch
                        }
                    }
                }
            } catch (_) {}
        }

        return { backendToken, branch, company, employee }
    }
}
